import React, { useEffect, useState, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Checkbox,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  MenuItem,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { getUsers, getRoles, setUserRoles } from '../api';
import type { UserOut, RoleOut } from '../types';
import MinimumLengthSearchField from '../../../shared/components/MinimumLengthSearchField';
import PaginationBar from '../../../shared/components/PaginationBar';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50];
const DEFAULT_PAGE_SIZE = 20;
const MINIMUM_SEARCH_LENGTH = 3;

const UsersPage: React.FC = () => {
  const [users, setUsers] = useState<UserOut[]>([]);
  const [roles, setRoles] = useState<RoleOut[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<number | ''>('');
  const [editUser, setEditUser] = useState<UserOut | null>(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState<number[]>([]);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);

  const loadUsers = useCallback(async (
    page: number = 1,
    searchTerm: string = '',
    requestedPageSize: number = pageSize,
    selectedRoleId: number | '' = '',
  ) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getUsers({
        page,
        page_size: requestedPageSize,
        search: searchTerm || undefined,
        ...(selectedRoleId ? { role_id: selectedRoleId } : {}),
      });
      setUsers(result.items);
      setCurrentPage(result.page);
      setTotalPages(result.total_pages);
      setTotalUsers(result.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  const loadRoles = async () => {
    try {
      const r = await getRoles();
      setRoles([...r].sort((a, b) => a.name.localeCompare(b.name)));
    } catch (_) {}
  };

  useEffect(() => {
    loadUsers(1, '');
  }, [loadUsers]);

  useEffect(() => {
    loadRoles();
  }, []);

  const handleSearch = () => {
    const trimmedSearchTerm = search.trim();
    if (trimmedSearchTerm.length > 0 && trimmedSearchTerm.length < MINIMUM_SEARCH_LENGTH) {
      return;
    }
    setSubmittedSearch(trimmedSearchTerm);
    setCurrentPage(1);
    loadUsers(1, trimmedSearchTerm, pageSize, roleFilter);
  };

  const handlePageChange = (newPage: number) => {
    loadUsers(newPage, submittedSearch, pageSize, roleFilter);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    loadUsers(1, submittedSearch, newPageSize, roleFilter);
  };

  const handleRoleFilterChange = (selectedRoleId: number | '') => {
    setRoleFilter(selectedRoleId);
    setCurrentPage(1);
    loadUsers(1, submittedSearch, pageSize, selectedRoleId);
  };

  const openEdit = (user: UserOut) => {
    setEditUser(user);
    const initialRoleIds = (user.role_ids && user.role_ids.length > 0)
      ? user.role_ids
      : (user.role_id ? [user.role_id] : []);
    setSelectedRoleIds(initialRoleIds);
  };

  const saveUser = async () => {
    if (!editUser) return;
    setSaving(true);
    try {
      await setUserRoles(editUser.user_id, selectedRoleIds);
      setEditUser(null);
      loadUsers(currentPage, submittedSearch, pageSize, roleFilter);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2, alignItems: 'flex-start' }}>
        <MinimumLengthSearchField
          placeholder="Search by email or name (min. 3 characters)..."
          value={search}
          onChange={setSearch}
          minimumLength={MINIMUM_SEARCH_LENGTH}
          sx={{ width: { xs: '100%', sm: 320 } }}
        />
        <TextField
          size="small"
          select
          label="Role"
          value={roleFilter}
          onChange={(e) => handleRoleFilterChange(e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ width: { xs: '100%', sm: 200 } }}
        >
          <MenuItem value="">All Roles</MenuItem>
          {roles.map((r) => (
            <MenuItem key={r.id} value={r.id}>
              {r.name}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>
        <Box sx={{ ml: { xs: 0, sm: 'auto' }, alignSelf: 'center' }}>
          <Chip
            label={`${totalUsers} ${totalUsers === 1 ? 'user' : 'users'}`}
            color="primary"
            size="small"
            variant="outlined"
            sx={{ fontWeight: 1000, px: 0.5 }}
          />
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, fontSize: 12, textTransform: 'uppercase', color: 'text.secondary' } }}>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Roles</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    Loading...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : users.map((user) => (
              <TableRow key={user.user_id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={700}>
                    {user.name ?? '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {user.roles && user.roles.length > 0 ? (
                      user.roles.map((r) => (
                        <Chip key={r.id} label={r.name} size="small" color="primary" variant="outlined" />
                      ))
                    ) : user.role_names && user.role_names.length > 0 ? (
                      user.role_names.map((rn) => (
                        <Chip key={rn} label={rn} size="small" color="primary" variant="outlined" />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {user.role_name ?? user.role ?? '—'}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon fontSize="small" />}
                    onClick={() => openEdit(user)}
                    sx={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}
                  >
                    Edit Roles
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && users.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary" align="center">
                    No users match your search.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2 }}>
        <PaginationBar
          page={currentPage}
          totalPages={totalPages}
          onChange={handlePageChange}
          rowsPerPage={pageSize}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          onRowsPerPageChange={handlePageSizeChange}
        />
      </Box>

      <Dialog open={Boolean(editUser)} onClose={() => setEditUser(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ px: 4, pt: 3, pb: 2 }}>
          <Typography variant="h6" fontWeight={700}>
            Manage Roles: <Box component="span" color="primary.main">{editUser?.email}</Box>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {selectedRoleIds.length} role{selectedRoleIds.length === 1 ? '' : 's'} selected
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 4, py: 1 }}>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
            Roles
          </Typography>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={roles}
            getOptionLabel={(r) => r.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            value={roles.filter((r) => selectedRoleIds.indexOf(r.id) > -1)}
            onChange={(_e, value) => setSelectedRoleIds(value.map((r) => r.id))}
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  checked={selected}
                  sx={{ mr: 1 }}
                />
                <Box>
                  <Typography variant="body2">{option.name}</Typography>
                  {option.description && (
                    <Typography variant="caption" color="text.secondary">
                      {option.description}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  label={option.name}
                  size="small"
                  color="primary"
                  sx={{ fontWeight: 700, borderRadius: 1.5 }}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => <TextField {...params} placeholder="Search roles..." sx={{ mt: 1 }} />}
          />
        </DialogContent>
        <DialogActions sx={{ px: 4, py: 3 }}>
          <Button onClick={() => setEditUser(null)} sx={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveUser}
            disabled={saving}
            sx={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, px: 3 }}
          >
            {saving ? 'Saving...' : 'Update Roles'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersPage;
